import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFitStore } from '../store/useFitStore';
import Button from '../components/shared/Button';
import Card from '../components/shared/Card';
import Input from '../components/shared/Input';
import VideoEmbed from '../components/shared/VideoEmbed';
import { isYoutubeUrl } from '../lib/youtube';

type Tab = 'programs' | 'exercises';

const Library: React.FC = () => {
  const navigate = useNavigate();
  const { masterPrograms, globalExercises, deleteMasterProgram, upsertGlobalExercise, showToast } = useFitStore();
  const [tab, setTab] = useState<Tab>('programs');
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [editingEx, setEditingEx] = useState<string | null>(null);
  const [editVideo, setEditVideo] = useState('');

  const countExercises = (workouts: { exercises: unknown[] }[]) =>
    workouts.reduce((n, w) => n + w.exercises.length, 0);

  const beginEdit = (id: string, current?: string) => {
    setEditingEx(id);
    setEditVideo(current || '');
  };
  const saveEdit = (id: string) => {
    const ex = globalExercises.find((e) => e.id === id);
    if (!ex) return;
    const url = editVideo.trim();
    if (url && !/^https?:\/\//i.test(url)) {
      showToast('Use a full URL (https://…)', 'error');
      return;
    }
    upsertGlobalExercise({ ...ex, videoDemoUrl: url || undefined });
    setEditingEx(null);
    showToast(url ? 'Demo video saved' : 'Demo video removed');
  };

  return (
    <div className="main-content">
      <header style={{ marginBottom: 'var(--spacing-lg)' }}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="hero text-white text-[44px] md:text-[48px] leading-[0.9] font-bold tracking-[-0.03em]">
              Library.
            </h1>
            <p className="text-fit-text-secondary text-[17px] md:text-[18px] font-medium mt-1">
              Your programs and exercises, all in one place.
            </p>
          </div>
          <Button onClick={() => navigate('/coach/program/new')}>+ New Program</Button>
        </div>
      </header>

      {/* Tabs — the two distinct "libraries" made explicit */}
      <div data-tour="library" className="planner-tabs">
        <div
          className={`planner-tab ${tab === 'programs' ? 'active' : ''}`}
          onClick={() => setTab('programs')}
        >
          Programs ({masterPrograms.length})
        </div>
        <div
          className={`planner-tab ${tab === 'exercises' ? 'active' : ''}`}
          onClick={() => setTab('exercises')}
        >
          Exercises ({globalExercises.length})
        </div>
      </div>

      {tab === 'programs' ? (
        masterPrograms.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {masterPrograms.map((program) => (
              <Card
                key={program.id}
                onClick={() => navigate(`/coach/program/${program.id}`)}
                className="p-5"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="font-bold text-[17px] tracking-tight leading-tight">{program.title}</h3>
                  <div className="flex items-center gap-2 shrink-0">
                    {program.isLocked ? (
                      <span className="bg-fit-bg-tertiary text-fit-yellow text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider">
                        Locked
                      </span>
                    ) : (
                      <button
                        onClick={(e) => { e.stopPropagation(); setConfirmId(program.id); }}
                        className="text-fit-muted hover:text-white text-base leading-none"
                        title="Delete program"
                        aria-label="Delete program"
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-fit-muted text-xs">
                  {program.workouts.length} workout{program.workouts.length === 1 ? '' : 's'} ·{' '}
                  {countExercises(program.workouts)} exercise{countExercises(program.workouts) === 1 ? '' : 's'}
                </p>
                {confirmId === program.id ? (
                  <div className="mt-5 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <span className="text-[11px] text-fit-text-secondary">Delete this program?</span>
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteMasterProgram(program.id); showToast('Program deleted'); setConfirmId(null); }}
                      className="text-[11px] font-semibold text-white underline underline-offset-2"
                    >
                      Yes
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); setConfirmId(null); }} className="text-[11px] text-fit-muted hover:text-white">Cancel</button>
                  </div>
                ) : (
                  <p className="mt-5 text-fit-text-secondary text-[11px] uppercase tracking-[0.12em] font-semibold">
                    Open editor →
                  </p>
                )}
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-10 text-center">
            <h3 className="text-lg font-semibold mb-1">No programs yet</h3>
            <p className="text-fit-text-secondary text-sm mb-6">
              Build your first training program — add workout days, snap in exercises, and link supersets.
            </p>
            <Button onClick={() => navigate('/coach/program/new')}>+ New Program</Button>
          </Card>
        )
      ) : globalExercises.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {globalExercises.map((ex) => (
            <Card key={ex.id} className="p-4 flex flex-col gap-3">
              <div>
                <h3 className="font-semibold text-[15px] tracking-tight mb-1">{ex.name}</h3>
                <p className="text-fit-muted text-xs">
                  {[ex.muscleGroup, ex.equipment].filter(Boolean).join(' · ')}
                </p>
              </div>
              {ex.videoDemoUrl ? (
                <VideoEmbed url={ex.videoDemoUrl} title={`${ex.name} demo`} />
              ) : (
                <p className="text-[11px] text-fit-muted italic">No demo video</p>
              )}
              {editingEx === ex.id ? (
                <div className="flex flex-col gap-2" onClick={(e) => e.stopPropagation()}>
                  <Input
                    value={editVideo}
                    onChange={(e) => setEditVideo(e.target.value)}
                    placeholder="https://youtube.com/watch?v=… or any video URL"
                    label="Demo video URL"
                  />
                  {editVideo && isYoutubeUrl(editVideo) && (
                    <div className="rounded-lg overflow-hidden border border-fit-border">
                      <VideoEmbed url={editVideo} title="Preview" />
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => saveEdit(ex.id)}>Save</Button>
                    <Button size="sm" variant="glass" onClick={() => setEditingEx(null)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => beginEdit(ex.id, ex.videoDemoUrl)}
                  className="self-start text-[11px] font-semibold text-fit-yellow hover:underline underline-offset-2"
                >
                  {ex.videoDemoUrl ? 'Edit video' : '+ Add demo video'}
                </button>
              )}
            </Card>
          ))}
        </div>
      ) : (
        <Card className="p-10 text-center">
          <h3 className="text-lg font-semibold mb-1">No exercises in your library yet</h3>
          <p className="text-fit-text-secondary text-sm">
            Exercises you add appear here and become searchable inside the program editor.
          </p>
        </Card>
      )}
    </div>
  );
};

export default Library;